import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

type Option = { value: string | number; label: string }

export default function Select({ value, onChange, options, placeholder, className, searchable, searchPlaceholder }: { value?: string | number; onChange: (v: string | number) => void; options: Option[]; placeholder?: string; className?: string; searchable?: boolean; searchPlaceholder?: string }) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const anchorRef = useRef<HTMLButtonElement>(null)
  const [pos, setPos] = useState<{top:number,left:number,width:number}>({ top: 0, left: 0, width: 0 })
  const [activeIndex, setActiveIndex] = useState<number>(-1)
  const [query, setQuery] = useState('')

  useEffect(() => {
    const h = (e: MouseEvent) => {
      if (!ref.current) return
      if (!ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', h)
    return () => document.removeEventListener('mousedown', h)
  }, [])

  useLayoutEffect(() => {
    const u = () => {
      if (!anchorRef.current) return
      const r = anchorRef.current.getBoundingClientRect()
      setPos({ top: r.top + window.scrollY + r.height + 8, left: r.left + window.scrollX, width: r.width })
    }
    if (open) {
      u()
      const s = () => u()
      window.addEventListener('scroll', s, true)
      window.addEventListener('resize', s, true)
      return () => {
        window.removeEventListener('scroll', s, true)
        window.removeEventListener('resize', s, true)
      }
    }
  }, [open])

  const filtered = query ? options.filter(o => String(o.label).toLowerCase().includes(query.toLowerCase())) : options

  const onKey = (e: React.KeyboardEvent) => {
    if (!open) return
    if (e.key === 'Escape') { setOpen(false); return }
    if (e.key === 'ArrowDown') { setActiveIndex(i => Math.min(filtered.length - 1, i + 1)); e.preventDefault(); return }
    if (e.key === 'ArrowUp') { setActiveIndex(i => Math.max(0, i - 1)); e.preventDefault(); return }
    if (e.key === 'Enter') { const o = filtered[activeIndex]; if (o) { onChange(o.value); setOpen(false) } }
  }

  const selected = options.find(o => o.value === value)

  return (
    <div ref={ref} className={`relative ${className || ''}`} onKeyDown={onKey}>
      <button ref={anchorRef} type="button" onClick={() => { setOpen(o => !o); setActiveIndex(Math.max(0, options.findIndex(o => o.value === value))) }} className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white text-sm flex items-center justify-between">
        <span>{selected ? selected.label : (placeholder || 'Selecionar')}</span>
        <i className="ri-arrow-down-s-line"></i>
      </button>
      {open && createPortal(
        <div className="fixed inset-0 z-[10000]" onMouseDown={() => setOpen(false)}>
          <div className="fixed bg-white text-black border border-gray-200 rounded-xl shadow-2xl" style={{ top: pos.top, left: pos.left, width: pos.width }} onMouseDown={(e) => e.stopPropagation()}>
            {searchable && (
              <div className="p-2 border-b border-gray-200">
                <input value={query} onChange={e => { setQuery(e.target.value); setActiveIndex(0) }} placeholder={searchPlaceholder || 'Pesquisar...'} className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm" />
              </div>
            )}
            <ul className="max-h-56 overflow-auto py-1">
              {filtered.map((o, idx) => (
                <li key={`${o.value}`}>
                  <button type="button" onClick={() => { onChange(o.value); setOpen(false); setQuery('') }} className={`w-full text-left px-4 py-2 text-sm ${o.value === value ? 'bg-gray-200 text-black' : idx===activeIndex ? 'bg-gray-100 text-black' : 'text-black hover:bg-gray-100'}`}>{o.label}</button>
                </li>
              ))}
              {filtered.length === 0 && (
                <li className="px-4 py-2 text-sm text-gray-500">Nenhuma opção</li>
              )}
            </ul>
          </div>
        </div>, document.body
      )}
    </div>
  )
}
